import React, { useState } from 'react';
import {
  Plus,
  Edit3,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info,
  Bell,
  Megaphone,
  Calendar,
  Clock,
  User,
  Trash2,
  Eye,
  EyeOff,
  Pin,
  PinOff,
  Loader
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';

const AdminNotifications = ({ currentUser }) => {
  const [selectedTab, setSelectedTab] = useState('notifications');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData());
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch notifications and announcements
  const { data, loading, error, refresh, lastRefresh } = useNotifications(currentUser);
  const { notifications = [], announcements = [] } = data || {};

  function getInitialFormData() {
    return {
      type: 'info',
      title: '',
      message: '',
      category: 'general',
      isPinned: false,
      isGlobal: true,
      targetRoles: 'all',
      targetDepartments: 'all',
      expiresAt: '',
      actionRequired: false,
      actionText: '',
      actionLink: '',
      attachments: '',
      status: 'Active'
    };
  }

  const roleOptions = [
    'all',
    'Operation',
    'PC',
    'Sales Agent',
    'Medical Representative',
    'HR',
    'Account',
    'Admin'
  ];

  const departmentOptions = [
    'all',
    'Operations',
    'Sales',
    'HR',
    'IT',
    'Finance',
    'Marketing',
    'Admin'
  ];

  const typeOptions = [
    { value: 'info', label: 'Info', color: 'bg-blue-100 text-blue-800' },
    { value: 'success', label: 'Success', color: 'bg-green-100 text-green-800' },
    { value: 'warning', label: 'Warning', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
  ];

  const categoryOptions = [
    'general', 'system', 'feature', 'performance', 'achievement', 
    'team', 'training', 'maintenance', 'security', 'policy'
  ];

  // Get filtered items
  const getFilteredItems = () => {
    let items = selectedTab === 'notifications' ? notifications : announcements;

    if (searchTerm) {
      items = items.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      items = items.filter(item => item.status === filterStatus);
    }

    if (filterType !== 'all') {
      items = items.filter(item => item.type === filterType);
    }

    return items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const filteredItems = getFilteredItems();

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_NOTIFICATIONS;
      const sheetName = selectedTab === 'notifications' ? 'Notifications' : 'Announcements';
      
      // Prepare data for submission
      const timestamp = editingItem ? editingItem.timestamp : new Date().toISOString();
      const id = editingItem ? editingItem.id : generateId();
      
      let rowData;
      if (selectedTab === 'notifications') {
        rowData = [
          id,
          formData.type,
          formData.title,
          formData.message,
          timestamp,
          currentUser.name,
          formData.category,
          formData.isPinned ? 'TRUE' : 'FALSE',
          formData.isGlobal ? 'TRUE' : 'FALSE',
          formData.targetRoles,
          formData.targetDepartments,
          formData.expiresAt,
          formData.actionRequired ? 'TRUE' : 'FALSE',
          formData.actionText,
          formData.actionLink,
          formData.status
        ];
      } else {
        rowData = [
          id,
          formData.type,
          formData.title,
          formData.message,
          timestamp,
          currentUser.name,
          formData.category,
          formData.isPinned ? 'TRUE' : 'FALSE',
          formData.isGlobal ? 'TRUE' : 'FALSE',
          formData.targetRoles,
          formData.targetDepartments,
          formData.expiresAt,
          formData.actionRequired ? 'TRUE' : 'FALSE',
          formData.actionText,
          formData.actionLink,
          formData.attachments,
          formData.status
        ];
      }

      if (editingItem) {
        // Update existing item
        await updateSheetRow(spreadsheetId, sheetName, editingItem.rowNumber, rowData);
      } else {
        // Add new item
        await appendToSheet(spreadsheetId, sheetName, rowData);
      }

      // Reset form and refresh data
      setFormData(getInitialFormData());
      setShowForm(false);
      setEditingItem(null);
      refresh();

    } catch (error) {
      console.error('Error saving item:', error);
      alert('Error saving item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Generate unique ID
  const generateId = () => {
    const prefix = selectedTab === 'notifications' ? 'notif-' : 'ann-';
    return prefix + Date.now().toString().slice(-6);
  };

  // Update sheet row
  const updateSheetRow = async (spreadsheetId, sheetName, rowNumber, data) => {
    if (!rowNumber || isNaN(rowNumber)) {
      throw new Error('Invalid row number for update operation');
    }
    
    const endColumn = selectedTab === 'notifications' ? 'P' : 'Q';
    const range = `${sheetName}!A${rowNumber}:${endColumn}${rowNumber}`;
    
    console.log('Updating row with range:', range);
    
    const response = await fetch('/api/sheets', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        spreadsheetId,
        range,
        values: [data]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to update item: ${errorData.error || response.statusText}`);
    }
    
    return response.json();
  };

  // Append to sheet
  const appendToSheet = async (spreadsheetId, sheetName, data) => {
    console.log('Appending data to sheet:', sheetName);
    
    const response = await fetch('/api/sheets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        spreadsheetId,
        sheetName,
        values: [data]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to create item: ${errorData.error || response.statusText}`);
    }
    
    return response.json();
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      type: item.type,
      title: item.title,
      message: item.message,
      category: item.category,
      isPinned: item.isPinned,
      isGlobal: item.isGlobal,
      targetRoles: Array.isArray(item.targetRoles) ? item.targetRoles.join(',') : item.targetRoles,
      targetDepartments: Array.isArray(item.targetDepartments) ? item.targetDepartments.join(',') : item.targetDepartments,
      expiresAt: item.expiresAt || '',
      actionRequired: item.actionRequired,
      actionText: item.actionText || '',
      actionLink: item.actionLink || '',
      attachments: item.attachments ? JSON.stringify(item.attachments) : '',
      status: item.status
    });
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (item) => {
    if (deleteConfirm !== item.id) {
      setDeleteConfirm(item.id);
      setTimeout(() => setDeleteConfirm(null), 3000);
      return;
    }

    try {
      setSaving(true);
      if (!item.rowNumber) {
        console.error('Missing rowNumber for item:', item);
        throw new Error('Cannot delete item: missing row number');
      }
      
      // Update status to 'Deleted' instead of actually deleting the row
      const updatedItem = { ...item, status: 'Deleted' };
      await handleQuickUpdate(updatedItem);
      
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error deleting item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Quick actions
  const togglePinned = async (item) => {
    const updatedItem = { ...item, isPinned: !item.isPinned };
    await handleQuickUpdate(updatedItem);
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === 'Active' ? 'Draft' : 'Active';
    const updatedItem = { ...item, status: newStatus };
    await handleQuickUpdate(updatedItem);
  };

  const handleQuickUpdate = async (updatedItem) => {
    try {
      setSaving(true);
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_NOTIFICATIONS;
      const sheetName = selectedTab === 'notifications' ? 'Notifications' : 'Announcements';
      
      if (!updatedItem.rowNumber) {
        console.error('Missing rowNumber for item:', updatedItem);
        throw new Error('Cannot update item: missing row number');
      }
      
      let rowData;
      if (selectedTab === 'notifications') {
        rowData = [
          updatedItem.id, 
          updatedItem.type, 
          updatedItem.title, 
          updatedItem.message,
          updatedItem.timestamp, 
          updatedItem.author, 
          updatedItem.category,
          updatedItem.isPinned ? 'TRUE' : 'FALSE',
          updatedItem.isGlobal ? 'TRUE' : 'FALSE',
          Array.isArray(updatedItem.targetRoles) ? updatedItem.targetRoles.join(',') : updatedItem.targetRoles,
          Array.isArray(updatedItem.targetDepartments) ? updatedItem.targetDepartments.join(',') : updatedItem.targetDepartments,
          updatedItem.expiresAt || '', 
          updatedItem.actionRequired ? 'TRUE' : 'FALSE',
          updatedItem.actionText || '', 
          updatedItem.actionLink || '', 
          updatedItem.status
        ];
      } else {
        rowData = [
          updatedItem.id, 
          updatedItem.type, 
          updatedItem.title, 
          updatedItem.message,
          updatedItem.timestamp, 
          updatedItem.author, 
          updatedItem.category,
          updatedItem.isPinned ? 'TRUE' : 'FALSE',
          updatedItem.isGlobal ? 'TRUE' : 'FALSE',
          Array.isArray(updatedItem.targetRoles) ? updatedItem.targetRoles.join(',') : updatedItem.targetRoles,
          Array.isArray(updatedItem.targetDepartments) ? updatedItem.targetDepartments.join(',') : updatedItem.targetDepartments,
          updatedItem.expiresAt || '', 
          updatedItem.actionRequired ? 'TRUE' : 'FALSE',
          updatedItem.actionText || '', 
          updatedItem.actionLink || '',
          updatedItem.attachments ? JSON.stringify(updatedItem.attachments) : '',
          updatedItem.status
        ];
      }

      await updateSheetRow(spreadsheetId, sheetName, updatedItem.rowNumber, rowData);
      refresh();
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Error updating item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'urgent': return AlertTriangle;
      case 'warning': return AlertTriangle;
      case 'info': return Info;
      case 'success': return CheckCircle;
      default: return Bell;
    }
  };

  const formatDate = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  // Helper: status badge class
  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'Active': return { background: 'rgba(34,197,94,0.12)', color: 'var(--status-success,#22c55e)' };
      case 'Draft': return { background: 'var(--gold-100,#fef3cd)', color: 'var(--gold-600,#a07020)' };
      case 'Expired': return { background: 'var(--surface-2)', color: 'var(--text-muted)' };
      default: return { background: 'rgba(239,68,68,0.1)', color: 'var(--status-error)' };
    }
  };

  // Helper: type border color
  const getTypeBorderColor = (type) => {
    switch (type) {
      case 'urgent': return 'var(--status-error)';
      case 'warning': return 'var(--gold-500,#c8a03c)';
      case 'success': return 'var(--status-success,#22c55e)';
      default: return 'var(--forest-400,#4a8c6e)';
    }
  };

  if (loading) {
    return (
      <div className="k-page">
        <div className="k-card k-loading">
          <div className="k-loading-spinner" />
          <p>Loading admin data…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-page">
        <div className="k-card k-error" style={{ textAlign: 'center', padding: '2rem' }}>
          <AlertTriangle size={36} style={{ margin: '0 auto 12px', color: 'var(--status-error)' }} />
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Error Loading Admin Data</p>
          <p style={{ marginBottom: 16 }}>{error}</p>
          <button className="k-btn-primary" onClick={refresh}>
            <RefreshCw size={14} style={{ display: 'inline', marginRight: 6 }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const allItems = [...notifications, ...announcements];
  const activeCount = allItems.filter(i => i.status === 'Active').length;
  const pinnedCount = allItems.filter(i => i.isPinned).length;

  return (
    <div className="k-page">

      {/* Refresh/action bar */}
      <div className="k-refresh-bar">
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : 'Admin: Notifications & Announcements'}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="k-btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => { setFormData(getInitialFormData()); setEditingItem(null); setShowForm(true); }}
          >
            <Plus size={14} /> Create New
          </button>
          <button
            className="k-btn-ghost"
            onClick={refresh}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw size={14} style={{ animation: saving ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="k-stats-grid">
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ background: 'var(--forest-100,#d4e8dd)' }}>
            <Bell size={20} style={{ color: 'var(--forest-600,#2d6a50)' }} />
          </div>
          <div>
            <div className="k-stat-value">{notifications.length}</div>
            <div className="k-stat-label">Notifications</div>
          </div>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ background: 'var(--forest-100,#d4e8dd)' }}>
            <Megaphone size={20} style={{ color: 'var(--forest-500,#3d7a5f)' }} />
          </div>
          <div>
            <div className="k-stat-value">{announcements.length}</div>
            <div className="k-stat-label">Announcements</div>
          </div>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ background: 'rgba(34,197,94,0.12)' }}>
            <CheckCircle size={20} style={{ color: 'var(--status-success,#22c55e)' }} />
          </div>
          <div>
            <div className="k-stat-value" style={{ color: 'var(--status-success,#22c55e)' }}>{activeCount}</div>
            <div className="k-stat-label">Active</div>
          </div>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ background: 'rgba(139,92,246,0.1)' }}>
            <Pin size={20} style={{ color: '#7c3aed' }} />
          </div>
          <div>
            <div className="k-stat-value" style={{ color: '#7c3aed' }}>{pinnedCount}</div>
            <div className="k-stat-label">Pinned</div>
          </div>
        </div>
      </div>

      {/* Tabs + filters */}
      <div className="k-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="k-tabs-wrap" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <div className="k-tabs-main" style={{ padding: '0 20px' }}>
            <button
              className={`k-tab-btn${selectedTab === 'notifications' ? ' active' : ''}`}
              onClick={() => setSelectedTab('notifications')}
            >
              Notifications <span className="k-tab-count">{notifications.length}</span>
            </button>
            <button
              className={`k-tab-btn${selectedTab === 'announcements' ? ' active' : ''}`}
              onClick={() => setSelectedTab('announcements')}
            >
              Announcements <span className="k-tab-count">{announcements.length}</span>
            </button>
          </div>
        </div>

        <div className="k-filter-bar" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-2)' }}>
          <input
            type="text"
            className="k-search"
            placeholder="Search by title, message, author…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 180 }}
          />
          <select className="k-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Draft">Draft</option>
            <option value="Expired">Expired</option>
            <option value="Deleted">Deleted</option>
          </select>
          <select className="k-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            {typeOptions.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        {/* Items list */}
        {filteredItems.length === 0 ? (
          <div className="k-empty" style={{ padding: '3rem' }}>
            {selectedTab === 'notifications'
              ? <Bell size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
              : <Megaphone size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
            }
            <p style={{ fontWeight: 600 }}>No Items Found</p>
            <p>{searchTerm || filterStatus !== 'all' || filterType !== 'all' ? 'No items match your filters' : `No ${selectedTab} available`}</p>
          </div>
        ) : (
          <div className="k-task-list">
            {filteredItems.map((item) => {
              const TypeIcon = getTypeIcon(item.type);
              const isDeleting = deleteConfirm === item.id;
              const statusStyle = getStatusBadgeStyle(item.status);

              return (
                <div
                  key={item.id}
                  className="k-task-row"
                  style={{ borderLeft: `3px solid ${getTypeBorderColor(item.type)}`, alignItems: 'flex-start' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="k-task-title" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                      <TypeIcon size={14} style={{ color: getTypeBorderColor(item.type), flexShrink: 0 }} />
                      <span className={`k-badge ${item.type === 'urgent' ? 'k-badge-urgent' : item.type === 'warning' ? 'k-badge-medium' : item.type === 'success' ? 'k-badge-low' : 'k-badge-role'}`}>
                        {item.type.toUpperCase()}
                      </span>
                      <span className="k-badge" style={statusStyle}>{item.status}</span>
                      {item.isPinned && <Pin size={12} style={{ color: 'var(--gold-500,#c8a03c)' }} />}
                      <span className="k-badge k-badge-role">ID: {item.id}</span>
                    </div>
                    <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', margin: '2px 0 4px' }}>{item.title}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', marginBottom: 6 }}>
                      {item.message}
                    </p>
                    <div className="k-task-meta">
                      <span className="k-task-meta-item"><User size={11} /> {item.author}</span>
                      <span className="k-task-meta-item"><Clock size={11} /> {formatDate(item.timestamp)}</span>
                      <span className="k-badge" style={{ background: 'rgba(139,92,246,0.1)', color: '#7c3aed' }}>{item.category}</span>
                      {item.expiresAt && (
                        <span className="k-task-meta-item" style={{ color: 'var(--status-warning,#f97316)' }}>
                          <Calendar size={11} /> Expires: {new Date(item.expiresAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 12, flexShrink: 0 }}>
                    <button
                      className="k-btn-ghost"
                      style={{ padding: '4px 7px', color: item.isPinned ? 'var(--gold-500,#c8a03c)' : 'var(--text-muted)' }}
                      onClick={() => togglePinned(item)}
                      title={item.isPinned ? 'Unpin' : 'Pin'}
                    >
                      {item.isPinned ? <PinOff size={14} /> : <Pin size={14} />}
                    </button>
                    <button
                      className="k-btn-ghost"
                      style={{ padding: '4px 7px', color: item.status === 'Active' ? 'var(--status-success,#22c55e)' : 'var(--text-muted)' }}
                      onClick={() => toggleStatus(item)}
                      title={item.status === 'Active' ? 'Set to Draft' : 'Activate'}
                    >
                      {item.status === 'Active' ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button
                      className="k-btn-ghost"
                      style={{ padding: '4px 7px', color: 'var(--forest-500,#3d7a5f)' }}
                      onClick={() => handleEdit(item)}
                      title="Edit"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      className="k-btn-ghost"
                      style={{ padding: '4px 7px', color: isDeleting ? 'var(--status-error)' : 'var(--text-muted)', background: isDeleting ? 'rgba(239,68,68,0.1)' : undefined }}
                      onClick={() => handleDelete(item)}
                      title={isDeleting ? 'Click again to confirm' : 'Delete'}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="k-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowForm(false); setEditingItem(null); setFormData(getInitialFormData()); } }}>
          <div className="k-modal" style={{ maxWidth: '52rem' }} onClick={(e) => e.stopPropagation()}>
            <div className="k-modal-header">
              <div>
                <h3 style={{ fontWeight: 700, fontSize: 17, color: 'var(--text-primary)', margin: 0 }}>
                  {editingItem ? 'Edit' : 'Create'} {selectedTab === 'notifications' ? 'Notification' : 'Announcement'}
                </h3>
              </div>
              <button
                className="k-modal-close"
                onClick={() => { setShowForm(false); setEditingItem(null); setFormData(getInitialFormData()); }}
              >
                ×
              </button>
            </div>

            <div className="k-modal-body">
              <form onSubmit={handleSubmit}>
                {/* Type + Category */}
                <div className="k-info-grid" style={{ marginBottom: 16 }}>
                  <div className="k-info-block">
                    <label className="k-field-label">Type</label>
                    <select className="k-select" style={{ width: '100%' }} value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} required>
                      {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="k-info-block">
                    <label className="k-field-label">Category</label>
                    <select className="k-select" style={{ width: '100%' }} value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required>
                      {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Title */}
                <div style={{ marginBottom: 14 }}>
                  <label className="k-field-label">Title</label>
                  <input
                    type="text"
                    className="k-search"
                    style={{ width: '100%', marginTop: 4 }}
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Enter title…"
                    required
                  />
                </div>

                {/* Message */}
                <div style={{ marginBottom: 14 }}>
                  <label className="k-field-label">Message</label>
                  <textarea
                    style={{ width: '100%', marginTop: 4, padding: '8px 12px', background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, resize: 'vertical', minHeight: 90 }}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Enter detailed message…"
                    required
                  />
                </div>

                {/* Target roles + departments */}
                <div className="k-info-grid" style={{ marginBottom: 14 }}>
                  <div className="k-info-block">
                    <label className="k-field-label">Target Roles</label>
                    <input type="text" className="k-search" style={{ width: '100%', marginTop: 4 }} value={formData.targetRoles} onChange={(e) => setFormData({...formData, targetRoles: e.target.value})} placeholder="all, Operation, PC… (comma-sep)" />
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Options: {roleOptions.join(', ')}</p>
                  </div>
                  <div className="k-info-block">
                    <label className="k-field-label">Target Departments</label>
                    <input type="text" className="k-search" style={{ width: '100%', marginTop: 4 }} value={formData.targetDepartments} onChange={(e) => setFormData({...formData, targetDepartments: e.target.value})} placeholder="all, Operations, Sales… (comma-sep)" />
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Options: {departmentOptions.join(', ')}</p>
                  </div>
                </div>

                {/* Expiry */}
                <div style={{ marginBottom: 14 }}>
                  <label className="k-field-label">Expires At (Optional)</label>
                  <input type="datetime-local" className="k-search" style={{ width: '100%', marginTop: 4 }} value={formData.expiresAt} onChange={(e) => setFormData({...formData, expiresAt: e.target.value})} />
                </div>

                {/* Action section */}
                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, marginBottom: 14 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}>
                    <input type="checkbox" checked={formData.actionRequired} onChange={(e) => setFormData({...formData, actionRequired: e.target.checked})} style={{ width: 14, height: 14 }} />
                    Requires Action Button
                  </label>
                  {formData.actionRequired && (
                    <div className="k-info-grid" style={{ marginTop: 10 }}>
                      <div className="k-info-block">
                        <label className="k-field-label">Action Text</label>
                        <input type="text" className="k-search" style={{ width: '100%', marginTop: 4 }} value={formData.actionText} onChange={(e) => setFormData({...formData, actionText: e.target.value})} placeholder="e.g., Acknowledge" />
                      </div>
                      <div className="k-info-block">
                        <label className="k-field-label">Action Link</label>
                        <input type="url" className="k-search" style={{ width: '100%', marginTop: 4 }} value={formData.actionLink} onChange={(e) => setFormData({...formData, actionLink: e.target.value})} placeholder="https://…" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Attachments (announcements only) */}
                {selectedTab === 'announcements' && (
                  <div style={{ marginBottom: 14 }}>
                    <label className="k-field-label">Attachments (JSON)</label>
                    <textarea
                      style={{ width: '100%', marginTop: 4, padding: '8px 12px', background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12, resize: 'vertical', minHeight: 56 }}
                      value={formData.attachments}
                      onChange={(e) => setFormData({...formData, attachments: e.target.value})}
                      placeholder='[{"name":"Report.pdf","url":"https://..."}]'
                    />
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>JSON array of {"{ name, url }"} objects</p>
                  </div>
                )}

                {/* Options */}
                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, marginBottom: 20 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}>
                      <input type="checkbox" checked={formData.isPinned} onChange={(e) => setFormData({...formData, isPinned: e.target.checked})} style={{ width: 14, height: 14 }} />
                      Pin to Top
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}>
                      <input type="checkbox" checked={formData.isGlobal} onChange={(e) => setFormData({...formData, isGlobal: e.target.checked})} style={{ width: 14, height: 14 }} />
                      Global (All Users)
                    </label>
                    <div>
                      <label className="k-field-label" style={{ display: 'block', marginBottom: 4 }}>Status</label>
                      <select className="k-select" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                        <option value="Active">Active</option>
                        <option value="Draft">Draft</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Form actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
                  <button
                    type="button"
                    className="k-btn-ghost"
                    onClick={() => { setShowForm(false); setEditingItem(null); setFormData(getInitialFormData()); }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="k-btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {saving ? (
                      <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                    ) : (
                      <><Save size={14} /> {editingItem ? 'Update' : 'Create'}</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
